import { useEffect, useMemo, useState } from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router';

import { PredefinedMilisecondsTypeEnum } from '../../../lib/enums/predefined-miliseconds-type.enum';
import lang from '../../../lib/language';
import { MarketCountryCode, StockExchangeCodeType } from '../../../lib/models/fundamental/types';
import { RootState } from '../../../lib/store';
import { AppCall } from '../../../lib/store/app/types';
import { FundamentalCall } from '../../../lib/store/fundamental/types';
import { hasValidCookie, mapUserFavouriteStocksData } from '../../../lib/store/selectors';
import {
  isCallStatusPending,
  isCallStatusReady,
} from '../../../lib/util/error-handling/StatusByCallHelpers';
import { isEmpty } from '../../../lib/util/typeGuards';

import ImageEmpty from '../../../assets/img/package-open.png';
import { useIsMobile, usePriceAndChartLoading } from '../../../hooks';
import { useSubscriptionManager } from '../../../hooks/useSubscriptionManager';
import DiscoverButton from '../../../shared_components/DiscoverButton/DiscoverButton';
import { UI_ROUTES } from '../../../util/routes';
import FavouritesModal from '../../StocksResult/componets/FavouritesModal';
import { WatchListSortingType } from '../WatchList.types';

import WatchListTable from './WatchListTable';

import styles from '../WatchList.module.scss';

type OwnProps = {
  sortingType: null | WatchListSortingType;
};
type Props = ReduxProps & OwnProps;

const FavouritesWatchList = (props: Props) => {
  const {
    hasCookieSession,
    favouriteStocks,
    sortingType,
    isLoadingUserPreferencesData,
    isMarketHoursReady,
    companyLogoAndNameData,
  } = props;

  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const [removedFavouriteSymbol, setRemovedFavouriteSymbol] = useState<string>('');
  const hasNoFavouriteStock = isEmpty(favouriteStocks);

  const dataStocks = favouriteStocks.map((item) => ({
    symbol: item.symbol,
    stockExchangeCode: item.stockExchangeCode,
    countryDesignation: item.countryDesignation,
  }));

  const subscriptionSymbols = useMemo(
    () =>
      favouriteStocks.map((stock) => {
        const companyInfo = companyLogoAndNameData[stock.symbol] ?? {};
        const countryDesignation = stock.countryDesignation
          || companyInfo.countryDesignation
          || MarketCountryCode.UnitedStates;
        const stockExchangeCode = stock.stockExchangeCode
          || companyInfo.stockExchangeCode
          || StockExchangeCodeType.US_NASDAQ;

        return {
          symbol: stock.symbol,
          stockExchangeCode,
          countryDesignation,
        };
      }),
    [favouriteStocks, companyLogoAndNameData],
  );

  const allMetadataLoaded = hasNoFavouriteStock
    || favouriteStocks.some(
      (item) => item.countryDesignation || companyLogoAndNameData[item.symbol]?.countryDesignation,
    )
    || favouriteStocks.some((item) => companyLogoAndNameData[item.symbol]);

  const enabled = subscriptionSymbols.length > 0
    && hasCookieSession
    && isMarketHoursReady
    && !isLoadingUserPreferencesData
    && allMetadataLoaded;

  useSubscriptionManager({
    symbols: subscriptionSymbols,
    enabled,
    subscriptionLabel: 'Favourites',
  });

  const isPriceAndChartLoading = usePriceAndChartLoading({
    stocks: dataStocks,
    isApiPending: isLoadingUserPreferencesData || !allMetadataLoaded,
  });

  const isLoading = !allMetadataLoaded || (!hasNoFavouriteStock && isPriceAndChartLoading);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (removedFavouriteSymbol) {
      timer = setTimeout(() => {
        setRemovedFavouriteSymbol('');
      }, PredefinedMilisecondsTypeEnum.eightSeconds);
    }

    return () => clearTimeout(timer);
  }, [removedFavouriteSymbol]);

  return (
    <>
      {hasNoFavouriteStock ? (
        <div className={styles.wrapperNoFavouritesStocks}>
          <img className={styles.noMessagesLogo} src={ImageEmpty} alt="no messages logo" />
          <div className={styles.noStocksMessage}>{lang.commonWatchlistFavoutesLimit()}</div>
          <div className={styles.noStocksMessageDescription}>
            {lang.commonMyAccountNoStocksMessage()}
          </div>
          <div className={styles.wrapperBtnDiscover}>
            <DiscoverButton
              text={lang.discoverStocks()}
              onClick={() => navigate(UI_ROUTES.discover)}
            />
          </div>
        </div>
      ) : (
        <WatchListTable
          sortingType={sortingType}
          hasSwipeableRows
          watchListStocks={dataStocks}
          isLoading={isLoading}
          title="favourites"
          onSymbolRemoved={setRemovedFavouriteSymbol}
        />
      )}
      {removedFavouriteSymbol && (
        <FavouritesModal
          isMobile={isMobile}
          isWatchlist
          isAddedToFavourites={false}
          isRemovedFromFavourites
          symbol={removedFavouriteSymbol}
          closeModal={() => setRemovedFavouriteSymbol('')}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  hasCookieSession: hasValidCookie(state),
  favouriteStocks: mapUserFavouriteStocksData(
    state.marketData.favouriteStocks,
    state.crm.individualExtendedInfo?.user_correlation_id,
  ),
  isMarketHoursReady: isCallStatusReady(
    FundamentalCall.getNonWorkingDaysAndMarketTime,
    state.fundamental.statusByCall,
  ),
  isLoadingUserPreferencesData: isCallStatusPending(
    AppCall.getUserPreferencesData,
    state.app.statusByCall,
  ),
  companyLogoAndNameData: state.fundamental.companyLogoAndName,
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(FavouritesWatchList);
